import { Controller } from "@hotwired/stimulus"

// facilities--floor-field — keeps a form's floor picker on the building picked
// in the same form.
//
// The floors are the BUILDING's (0 … floor_count − 1, the basements in use,
// which ones already have a plan), so the picker cannot be rendered until the
// building is known, and shipping every building's floors with the page would
// not scale (a building can declare 999). Instead the picker sits in a Turbo
// Frame, and each building change points that frame at
// `Facilities::Admin::BuildingFloorsController`, which answers the picker for
// that building — or a disabled one when the building is cleared.
//
// It listens for `change` on the whole form because the building select is
// rendered by the engine's location cascade, which takes no host attributes,
// and it reads the building select's VALUE rather than the event's target: a
// cascade that narrows the buildings when the site changes can clear the
// building without a `change` of its own, and the floors must follow it. The
// floor picker's own `change` (it bubbles here too) leaves the building as it
// was, so it reloads nothing.
//
// Values:
//   url            the endpoint, with every parameter but the building
//   frameId        the Turbo Frame holding the floor picker
//   buildingField  the building select's `name`, e.g. "drawing[building_id]"
export default class extends Controller {
  static values = { url: String, frameId: String, buildingField: String }

  // The building the picker was last rendered for. Tom Select and the engine's
  // cascade can both report one pick as `change`, and a second load of the
  // same floors would swap the picker out from under a click that is already
  // on its way.
  connect() {
    this.building = this.#buildingSelect()?.value || ""
  }

  /** data-action="change->facilities--floor-field#buildingChanged" */
  buildingChanged() {
    const building = this.#buildingSelect()?.value || ""
    if (building === this.building) return
    this.building = building

    const frame = document.getElementById(this.frameIdValue)
    if (!frame) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("building_id", building)
    frame.src = url.toString()
  }

  #buildingSelect() {
    return this.element.querySelector(`[name="${this.buildingFieldValue}"]`)
  }
}
